<template>
  <div class="documents-page">
    <!-- Page Header -->
    <div class="d-flex flex-column flex-md-row justify-space-between align-center mb-6">
      <div class="mb-4 mb-md-0">
        <h1 class="text-h4 font-weight-bold text-primary">
          Document Management
        </h1>
        <p class="text-subtitle-1 text-medium-emphasis">
          Centralized document repository for board meetings and governance
        </p>
      </div>

      <div class="d-flex align-center gap-2">
        <!-- Manage Categories Button (Admin only) -->
        <v-btn
          v-if="isAdmin"
          color="info"
          variant="outlined"
          prepend-icon="mdi-folder-cog"
          @click="showCategoriesDialog = true"
        >
          Manage Categories
        </v-btn>

        <!-- View Toggle -->
        <v-btn-toggle
          v-model="viewMode"
          color="primary"
          variant="outlined"
          divided
          mandatory
        >
          <v-btn value="grid" size="small">
            <v-icon>mdi-view-grid</v-icon>
            <span class="d-none d-md-inline ml-1">Grid</span>
          </v-btn>
          <v-btn value="list" size="small">
            <v-icon>mdi-view-list</v-icon>
            <span class="d-none d-md-inline ml-1">List</span>
          </v-btn>
        </v-btn-toggle>

        <!-- Upload Button -->
        <v-btn
          color="primary"
          prepend-icon="mdi-upload"
          @click="showUploadDialog = true"
        >
          Upload Document
        </v-btn>
      </div>
    </div>

    <!-- Filters and Search -->
    <v-card class="mb-6" flat>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="searchQuery"
              prepend-inner-icon="mdi-magnify"
              label="Search documents..."
              variant="outlined"
              density="compact"
              hide-details
              clearable
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-select
              v-model="categoryFilter"
              :items="categories"
              item-title="name"
              item-value="id"
              label="Category"
              variant="outlined"
              density="compact"
              hide-details
              clearable
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-select
              v-model="typeFilter"
              :items="documentTypes"
              label="Type"
              variant="outlined"
              density="compact"
              hide-details
              clearable
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-select
              v-model="statusFilter"
              :items="statusOptions"
              label="Status"
              variant="outlined"
              density="compact"
              hide-details
              clearable
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-btn
              color="primary"
              variant="outlined"
              block
              @click="clearFilters"
            >
              Clear Filters
            </v-btn>
          </v-col>
        </v-row>

        <!-- Quick Filter Chips -->
        <div class="mt-4">
          <v-chip-group
            v-model="quickFilter"
            color="primary"
            variant="outlined"
            multiple
            selected-class="text-primary"
          >
            <v-chip value="recent">Recent</v-chip>
            <v-chip value="my-documents">My Documents</v-chip>
            <v-chip value="shared">Shared with Me</v-chip>
            <v-chip value="starred">Starred</v-chip>
            <v-chip value="needs-review">Needs Review</v-chip>
          </v-chip-group>
        </div>
      </v-card-text>
    </v-card>

    <!-- Documents Display -->
    <v-card>
      <!-- Grid View -->
      <div v-if="viewMode === 'grid'" class="pa-4">
        <v-row>
          <v-col
            v-for="document in filteredDocuments"
            :key="document.id"
            cols="12"
            sm="6"
            md="4"
            lg="3"
          >
            <v-card
              class="document-card"
              hover
              @click="viewDocument(document)"
            >
              <div class="document-preview">
                <v-icon
                  size="48"
                  :color="getDocumentColor(document.file_type)"
                >
                  {{ getDocumentIcon(document.file_type) }}
                </v-icon>
                <div class="document-overlay">
                  <v-btn
                    icon
                    size="small"
                    variant="text"
                    color="white"
                    @click.stop="downloadDocument(document)"
                  >
                    <v-icon>mdi-download</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    size="small"
                    variant="text"
                    color="white"
                    @click.stop="toggleStar(document)"
                  >
                    <v-icon>{{ document.is_starred ? 'mdi-star' : 'mdi-star-outline' }}</v-icon>
                  </v-btn>
                </div>
              </div>

              <v-card-text class="pa-3">
                <div class="document-title">{{ document.title }}</div>
                <div class="document-meta">
                  <div class="text-caption text-medium-emphasis">
                    {{ formatFileSize(document.file_size) }} • {{ document.file_type }}
                  </div>
                  <div class="text-caption text-medium-emphasis">
                    {{ formatDate(document.created_at) }}
                  </div>
                </div>

                <div class="mt-2">
                  <v-chip
                    v-if="document.status"
                    :color="getStatusColor(document.status)"
                    :prepend-icon="getStatusIcon(document.status)"
                    size="x-small"
                    variant="flat"
                  >
                    {{ getStatusText(document.status) }}
                  </v-chip>
                  <v-chip
                    v-if="document.category"
                    color="grey"
                    size="x-small"
                    variant="outlined"
                    class="ml-1"
                  >
                    {{ document.category.name }}
                  </v-chip>
                </div>
              </v-card-text>

              <v-card-actions class="pa-2">
                <v-spacer />
                <v-menu>
                  <template #activator="{ props }">
                    <v-btn
                      icon
                      size="small"
                      variant="text"
                      v-bind="props"
                      @click.stop
                    >
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list density="compact">
                    <v-list-item @click="viewDocument(document)">
                      <v-list-item-title>View</v-list-item-title>
                      <template #prepend>
                        <v-icon>mdi-eye</v-icon>
                      </template>
                    </v-list-item>
                    <v-list-item @click="downloadDocument(document)">
                      <v-list-item-title>Download</v-list-item-title>
                      <template #prepend>
                        <v-icon>mdi-download</v-icon>
                      </template>
                    </v-list-item>
                    <v-list-item @click="shareDocument(document)">
                      <v-list-item-title>Share</v-list-item-title>
                      <template #prepend>
                        <v-icon>mdi-share</v-icon>
                      </template>
                    </v-list-item>
                    <v-list-item @click="editDocument(document)">
                      <v-list-item-title>Edit</v-list-item-title>
                      <template #prepend>
                        <v-icon>mdi-pencil</v-icon>
                      </template>
                    </v-list-item>
                    <v-divider />
                    <v-list-item
                      @click="deleteDocument(document)"
                      class="text-error"
                    >
                      <v-list-item-title>Delete</v-list-item-title>
                      <template #prepend>
                        <v-icon>mdi-delete</v-icon>
                      </template>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </div>

      <!-- List View -->
      <div v-else>
        <v-data-table
          :headers="listHeaders"
          :items="filteredDocuments"
          :loading="loading"
          :search="searchQuery"
          item-value="id"
          hover
        >
          <template #item.title="{ item }">
            <div class="d-flex align-center">
              <v-icon
                :color="getDocumentColor(item.file_type)"
                class="mr-3"
              >
                {{ getDocumentIcon(item.file_type) }}
              </v-icon>
              <div>
                <div class="font-weight-medium cursor-pointer text-primary" @click="viewDocument(item)">
                  {{ item.title }}
                </div>
                <div class="text-caption text-medium-emphasis">
                  {{ item.description }}
                </div>
              </div>
            </div>
          </template>

          <template #item.file_info="{ item }">
            <div>
              <div class="text-body-2">{{ item.file_type ? item.file_type.toUpperCase() : 'UNKNOWN' }}</div>
              <div class="text-caption text-medium-emphasis">
                {{ formatFileSize(item.file_size) }}
              </div>
            </div>
          </template>

          <template #item.category="{ item }">
            <v-chip
              v-if="item.category"
              color="grey"
              size="small"
              variant="outlined"
            >
              {{ item.category.name }}
            </v-chip>
          </template>

          <template #item.status="{ item }">
            <v-chip
              v-if="item.status"
              :color="getStatusColor(item.status)"
              :prepend-icon="getStatusIcon(item.status)"
              size="small"
              variant="flat"
            >
              {{ getStatusText(item.status) }}
            </v-chip>
            <span v-else class="text-caption text-medium-emphasis">No Status</span>
          </template>

          <template #item.uploaded_by="{ item }">
            <div>
              <div class="text-body-2">{{ item.uploader ? item.uploader.full_name : 'Unknown User' }}</div>
              <div class="text-caption text-medium-emphasis">
                {{ formatDate(item.created_at) }}
              </div>
            </div>
          </template>

          <template #item.actions="{ item }">
            <div class="d-flex align-center gap-1">
              <v-btn
                icon
                size="small"
                variant="text"
                @click="toggleStar(item)"
              >
                <v-icon>{{ item.is_starred ? 'mdi-star' : 'mdi-star-outline' }}</v-icon>
              </v-btn>
              <v-btn
                icon
                size="small"
                variant="text"
                @click="downloadDocument(item)"
              >
                <v-icon>mdi-download</v-icon>
              </v-btn>
              <v-menu>
                <template #activator="{ props }">
                  <v-btn
                    icon
                    size="small"
                    variant="text"
                    v-bind="props"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-item @click="viewDocument(item)">
                    <v-list-item-title>View</v-list-item-title>
                    <template #prepend>
                      <v-icon>mdi-eye</v-icon>
                    </template>
                  </v-list-item>
                  <v-list-item @click="shareDocument(item)">
                    <v-list-item-title>Share</v-list-item-title>
                    <template #prepend>
                      <v-icon>mdi-share</v-icon>
                    </template>
                  </v-list-item>
                  <v-list-item @click="editDocument(item)">
                    <v-list-item-title>Edit</v-list-item-title>
                    <template #prepend>
                      <v-icon>mdi-pencil</v-icon>
                    </template>
                  </v-list-item>
                  <v-divider />
                  <v-list-item @click="deleteDocument(item)" class="text-error">
                    <v-list-item-title>Delete</v-list-item-title>
                    <template #prepend>
                      <v-icon>mdi-delete</v-icon>
                    </template>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </template>
        </v-data-table>
      </div>
    </v-card>

    <!-- Upload Document Dialog -->
    <v-dialog v-model="showUploadDialog" max-width="800" scrollable>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span class="text-h5">Upload Document</span>
          <v-btn
            icon
            variant="text"
            @click="showUploadDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-form ref="uploadForm" v-model="uploadFormValid" @submit.prevent="uploadDocument">
            <!-- File Upload Area -->
            <div class="upload-area mb-6" @drop="handleFileDrop" @dragover.prevent @dragenter.prevent>
              <input
                ref="fileInput"
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                @change="handleFileSelect"
                style="display: none;"
              >
              
              <div v-if="selectedFiles.length === 0" class="upload-placeholder" @click="$refs.fileInput.click()">
                <v-icon size="64" color="grey">mdi-cloud-upload</v-icon>
                <h3 class="text-h6 mt-4 mb-2">Drop files here or click to browse</h3>
                <p class="text-body-2 text-medium-emphasis">
                  Supported formats: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT
                </p>
                <p class="text-caption text-medium-emphasis">
                  Maximum file size: 50MB per file
                </p>
              </div>

              <div v-else class="selected-files">
                <div
                  v-for="(file, index) in selectedFiles"
                  :key="index"
                  class="file-item"
                >
                  <v-icon :color="getDocumentColor(getFileExtension(file.name))">
                    {{ getDocumentIcon(getFileExtension(file.name)) }}
                  </v-icon>
                  <div class="file-info">
                    <div class="file-name">{{ file.name }}</div>
                    <div class="file-size text-caption">{{ formatFileSize(file.size) }}</div>
                  </div>
                  <v-btn
                    icon
                    size="small"
                    variant="text"
                    @click="removeFile(index)"
                  >
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </div>
                
                <v-btn
                  variant="outlined"
                  @click="$refs.fileInput.click()"
                  class="mt-3"
                >
                  Add More Files
                </v-btn>
              </div>
            </div>

            <!-- Document Metadata -->
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="uploadForm.title"
                  label="Document Title *"
                  :rules="titleRules"
                  variant="outlined"
                  required
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="uploadForm.category_id"
                  :items="categories"
                  item-title="name"
                  item-value="id"
                  label="Category *"
                  :rules="categoryRules"
                  variant="outlined"
                  required
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="uploadForm.description"
                  label="Description"
                  variant="outlined"
                  rows="3"
                  auto-grow
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="uploadForm.access_level"
                  :items="accessLevels"
                  label="Access Level *"
                  :rules="accessRules"
                  variant="outlined"
                  required
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="uploadForm.meeting_id"
                  :items="meetings"
                  item-title="title"
                  item-value="id"
                  label="Related Meeting (Optional)"
                  variant="outlined"
                  clearable
                />
              </v-col>
            </v-row>

            <!-- Tags -->
            <v-combobox
              v-model="uploadForm.tags"
              label="Tags"
              variant="outlined"
              multiple
              chips
              closable-chips
              hint="Add tags to help organize and find documents"
              persistent-hint
            />
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn @click="showUploadDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!uploadFormValid || selectedFiles.length === 0"
            :loading="uploading"
            @click="uploadDocument"
          >
            Upload {{ selectedFiles.length }} File{{ selectedFiles.length !== 1 ? 's' : '' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Document Viewer Dialog -->
    <v-dialog v-model="showViewerDialog" max-width="1200" scrollable>
      <v-card v-if="selectedDocument">
        <v-card-title class="d-flex justify-space-between align-center">
          <span class="text-h6">{{ selectedDocument.title }}</span>
          <div class="d-flex align-center gap-2">
            <v-btn
              icon
              variant="text"
              @click="downloadDocument(selectedDocument)"
            >
              <v-icon>mdi-download</v-icon>
            </v-btn>
            <v-btn
              icon
              variant="text"
              @click="closeViewerDialog"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text class="pa-0">
          <!-- PDF Viewer or File Preview -->
          <div class="document-viewer">
            <iframe
              v-if="selectedDocument.file_type === 'pdf' && previewBlobUrl"
              :src="previewBlobUrl"
              width="100%"
              height="600px"
              frameborder="0"
            />
            <div v-else-if="selectedDocument.file_type === 'pdf' && !previewBlobUrl" class="preview-placeholder">
              <v-progress-circular indeterminate color="primary" />
              <p class="text-body-2 mt-4">Loading preview…</p>
            </div>
            <div v-else class="preview-placeholder">
              <v-icon size="96" color="grey">
                {{ getDocumentIcon(selectedDocument.file_type) }}
              </v-icon>
              <h3 class="text-h6 mt-4">{{ selectedDocument.title }}</h3>
              <p class="text-body-2 text-medium-emphasis mb-4">
                Preview not available for {{ selectedDocument.file_type ? selectedDocument.file_type.toUpperCase() : 'UNKNOWN' }} files
              </p>
              <v-btn
                color="primary"
                variant="outlined"
                @click="downloadDocument(selectedDocument)"
              >
                Download to View
              </v-btn>
            </div>
          </div>
        </v-card-text>

        <!-- Document Comments -->
        <v-card-text>
          <v-divider class="mb-4" />
          <h4 class="text-h6 mb-4">Comments</h4>
          
          <!-- Add Comment -->
          <div class="mb-4">
            <v-textarea
              v-model="newComment"
              label="Add a comment..."
              variant="outlined"
              rows="2"
              auto-grow
            />
            <div class="text-right mt-2">
              <v-btn
                color="primary"
                size="small"
                :disabled="!newComment.trim()"
                @click="addComment"
              >
                Add Comment
              </v-btn>
            </div>
          </div>

          <!-- Comments List -->
          <div v-if="documentComments.length > 0">
            <div
              v-for="comment in documentComments"
              :key="comment.id"
              class="comment-item mb-3"
            >
              <div class="d-flex align-start">
                <v-avatar size="32" color="primary" class="mr-3">
                  <span class="text-caption text-white">{{ comment.author ? getInitials(comment.author.full_name) : 'U' }}</span>
                </v-avatar>
                <div class="flex-grow-1">
                  <div class="d-flex align-center mb-1">
                    <span class="font-weight-medium">{{ comment.author ? comment.author.full_name : 'Unknown User' }}</span>
                    <span class="text-caption text-medium-emphasis ml-2">
                      {{ formatDate(comment.created_at) }}
                    </span>
                  </div>
                  <p class="text-body-2 mb-0">{{ comment.content }}</p>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="text-center text-medium-emphasis py-4">
            No comments yet. Be the first to comment!
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Share Document Dialog -->
    <v-dialog v-model="showShareDialog" max-width="600">
      <v-card v-if="selectedDocument">
        <v-card-title>Share "{{ selectedDocument.title }}"</v-card-title>
        <v-card-text>
          <v-select
            v-model="shareForm.users"
            :items="boardMembers"
            item-title="full_name"
            item-value="id"
            label="Share with board members"
            variant="outlined"
            multiple
            chips
            closable-chips
          />
          
          <v-select
            v-model="shareForm.permission"
            :items="sharePermissions"
            label="Permission Level"
            variant="outlined"
            class="mt-4"
          />

          <v-textarea
            v-model="shareForm.message"
            label="Message (Optional)"
            variant="outlined"
            rows="3"
            class="mt-4"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="showShareDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="shareDocument">Share</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">Delete Document</v-card-title>
        <v-card-text>
          Are you sure you want to delete "{{ selectedDocument?.title }}"? This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn
            color="error"
            :loading="deleting"
            @click="confirmDelete"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Document Categories Management Dialog -->
    <v-dialog v-model="showCategoriesDialog" max-width="800" scrollable>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span class="text-h5">Manage Document Categories</span>
          <v-btn
            icon
            variant="text"
            @click="showCategoriesDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <!-- Add New Category Form -->
          <v-card class="mb-6" flat border>
            <v-card-title class="text-h6">Add New Category</v-card-title>
            <v-card-text>
              <v-form ref="categoryForm" v-model="categoryFormValid" @submit.prevent="addCategory">
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="newCategory.name"
                      label="Category Name *"
                      :rules="categoryNameRules"
                      variant="outlined"
                      density="compact"
                      required
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="newCategory.color"
                      label="Color"
                      type="color"
                      variant="outlined"
                      density="compact"
                      style="max-width: 120px;"
                    />
                  </v-col>
                  <v-col cols="12" md="3" class="d-flex align-center">
                    <v-btn
                      type="submit"
                      color="primary"
                      :disabled="!categoryFormValid"
                      :loading="addingCategory"
                    >
                      Add Category
                    </v-btn>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <v-textarea
                      v-model="newCategory.description"
                      label="Description (Optional)"
                      variant="outlined"
                      density="compact"
                      rows="2"
                      auto-grow
                    />
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
          </v-card>

          <!-- Existing Categories List -->
          <v-card flat border>
            <v-card-title class="text-h6">Existing Categories</v-card-title>
            <v-card-text>
              <v-list>
                <v-list-item
                  v-for="category in categories"
                  :key="category.id"
                  class="category-item"
                >
                  <template #prepend>
                    <div
                      class="category-color"
                      :style="{ backgroundColor: category.color || '#1565C0' }"
                    ></div>
                  </template>

                  <v-list-item-title>{{ category.name }}</v-list-item-title>
                  <v-list-item-subtitle v-if="category.description">
                    {{ category.description }}
                  </v-list-item-subtitle>

                  <template #append>
                    <div class="d-flex align-center gap-1">
                      <v-btn
                        icon
                        size="small"
                        variant="text"
                        @click="editCategory(category)"
                      >
                        <v-icon>mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        size="small"
                        variant="text"
                        color="error"
                        @click="deleteCategory(category)"
                      >
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </div>
                  </template>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn @click="showCategoriesDialog = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Category Dialog -->
    <v-dialog v-model="editCategoryDialog.show" max-width="500">
      <v-card>
        <v-card-title>Edit Category</v-card-title>
        <v-card-text>
          <v-form ref="editCategoryForm" v-model="editCategoryFormValid" @submit.prevent="updateCategory">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="editCategoryDialog.category.name"
                  label="Category Name *"
                  :rules="categoryNameRules"
                  variant="outlined"
                  required
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="editCategoryDialog.category.color"
                  label="Color"
                  type="color"
                  variant="outlined"
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="editCategoryDialog.category.description"
                  label="Description"
                  variant="outlined"
                  rows="2"
                  auto-grow
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="editCategoryDialog.show = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!editCategoryFormValid"
            :loading="updatingCategory"
            @click="updateCategory"
          >
            Update
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Category Confirmation -->
    <v-dialog v-model="deleteCategoryDialog.show" max-width="400">
      <v-card>
        <v-card-title class="text-h6">Delete Category</v-card-title>
        <v-card-text>
          Are you sure you want to delete "{{ deleteCategoryDialog.category?.name }}"? 
          This action cannot be undone and may affect existing documents.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="deleteCategoryDialog.show = false">
            Cancel
          </v-btn>
          <v-btn
            color="error"
            :loading="deletingCategory"
            @click="confirmDeleteCategory"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { useAuthStore } from '@/store/auth'
import api from '@/services/api'

export default {
  name: 'Documents',
  setup() {
    const router = useRouter()
    const toast = useToast()
    const authStore = useAuthStore()

    // State
    const loading = ref(false)
    const documents = ref([])
    const categories = ref([])
    const meetings = ref([])
    const boardMembers = ref([])
    const viewMode = ref('grid')
    const searchQuery = ref('')
    const categoryFilter = ref('')
    const typeFilter = ref('')
    const statusFilter = ref('')
    const quickFilter = ref([])
    
    // Get admin status from auth store
    const isAdmin = computed(() => authStore.user?.is_admin || false)

    // Dialog states
    const showUploadDialog = ref(false)
    const showViewerDialog = ref(false)
    const showShareDialog = ref(false)
    const showDeleteDialog = ref(false)
    const showCategoriesDialog = ref(false)
    const selectedDocument = ref(null)
    const previewBlobUrl = ref(null)
    const uploading = ref(false)
    const deleting = ref(false)

    // Category Management
    const categoryFormValid = ref(false)
    const editCategoryFormValid = ref(false)
    const addingCategory = ref(false)
    const updatingCategory = ref(false)
    const deletingCategory = ref(false)

    const newCategory = ref({
      name: '',
      description: '',
      color: '#1565C0'
    })

    const editCategoryDialog = ref({
      show: false,
      category: {
        id: null,
        name: '',
        description: '',
        color: '#1565C0'
      }
    })

    const deleteCategoryDialog = ref({
      show: false,
      category: null
    })

    // Validation rules for categories
    const categoryNameRules = [
      v => !!v || 'Category name is required',
      v => v.length >= 2 || 'Name must be at least 2 characters',
      v => v.length <= 50 || 'Name must be less than 50 characters'
    ]

    // Upload form
    const uploadFormValid = ref(false)
    const selectedFiles = ref([])
    const uploadForm = ref({
      title: '',
      description: '',
      category_id: '',
      status: 'active',
      access_level: 'board_members',
      meeting_id: '',
      tags: []
    })

    // Comments
    const documentComments = ref([])
    const newComment = ref('')

    // Share form
    const shareForm = ref({
      users: [],
      permission: 'view',
      message: ''
    })

    // Reference data
    const documentTypes = [
      'PDF', 'DOC', 'DOCX', 'XLS', 'XLSX', 'PPT', 'PPTX', 'TXT'
    ]

    const statusOptions = [
      'active', 'draft', 'review', 'approved', 'archived', 'pending', 'rejected'
    ]

    const accessLevels = [
      { title: 'Board Members Only', value: 'board_members' },
      { title: 'Public', value: 'public' },
      { title: 'Restricted', value: 'restricted' }
    ]

    const sharePermissions = [
      { title: 'View Only', value: 'view' },
      { title: 'Comment', value: 'comment' },
      { title: 'Edit', value: 'edit' }
    ]

    // Table headers for list view
    const listHeaders = [
      { title: 'Document', key: 'title', sortable: true },
      { title: 'Type/Size', key: 'file_info', sortable: false },
      { title: 'Category', key: 'category', sortable: true },
      { title: 'Status', key: 'status', sortable: true },
      { title: 'Uploaded By', key: 'uploaded_by', sortable: true },
      { title: 'Actions', key: 'actions', sortable: false, width: '120px' }
    ]

    // Validation rules
    const titleRules = [
      v => !!v || 'Title is required',
      v => v.length >= 3 || 'Title must be at least 3 characters'
    ]

    const categoryRules = [
      v => !!v || 'Category is required'
    ]

    const accessRules = [
      v => !!v || 'Access level is required'
    ]

    // Computed properties
    const filteredDocuments = computed(() => {
      let filtered = documents.value

      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        filtered = filtered.filter(doc =>
          doc.title.toLowerCase().includes(query) ||
          doc.description?.toLowerCase().includes(query) ||
          doc.tags?.some(tag => tag.toLowerCase().includes(query))
        )
      }

      if (categoryFilter.value) {
        filtered = filtered.filter(doc => doc.category_id === categoryFilter.value)
      }

      if (typeFilter.value) {
        filtered = filtered.filter(doc => doc.file_type === typeFilter.value.toLowerCase())
      }

      if (statusFilter.value) {
        filtered = filtered.filter(doc => doc.status === statusFilter.value)
      }

      // Apply quick filters
      if (quickFilter.value.includes('recent')) {
        const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
        filtered = filtered.filter(doc => new Date(doc.created_at) > weekAgo)
      }

      if (quickFilter.value.includes('my-documents')) {
        // Filter for current user's documents
        filtered = filtered.filter(doc => doc.uploaded_by.id === 1) // Replace with actual user ID
      }

      if (quickFilter.value.includes('starred')) {
        filtered = filtered.filter(doc => doc.is_starred)
      }

      if (quickFilter.value.includes('needs-review')) {
        filtered = filtered.filter(doc => doc.status === 'review')
      }

      return filtered
    })

    // Methods
    const clearFilters = () => {
      searchQuery.value = ''
      categoryFilter.value = ''
      typeFilter.value = ''
      statusFilter.value = ''
      quickFilter.value = []
    }

    const getDocumentIcon = (fileType) => {
      const icons = {
        pdf: 'mdi-file-pdf-box',
        doc: 'mdi-file-word-box',
        docx: 'mdi-file-word-box',
        xls: 'mdi-file-excel-box',
        xlsx: 'mdi-file-excel-box',
        ppt: 'mdi-file-powerpoint-box',
        pptx: 'mdi-file-powerpoint-box',
        txt: 'mdi-file-document-outline'
      }
      return icons[fileType?.toLowerCase()] || 'mdi-file-document'
    }

    const getDocumentColor = (fileType) => {
      const colors = {
        pdf: '#F44336',
        doc: '#2196F3',
        docx: '#2196F3',
        xls: '#4CAF50',
        xlsx: '#4CAF50',
        ppt: '#FF9800',
        pptx: '#FF9800',
        txt: '#9E9E9E'
      }
      return colors[fileType?.toLowerCase()] || '#757575'
    }

    const getStatusColor = (status) => {
      if (!status) return 'grey'
      const colors = {
        active: 'success',
        draft: 'grey-darken-1', 
        review: 'warning',
        approved: 'success',
        archived: 'blue-grey',
        pending: 'orange',
        rejected: 'error'
      }
      return colors[status] || 'grey'
    }

    const getStatusIcon = (status) => {
      if (!status) return 'mdi-help-circle'
      const icons = {
        active: 'mdi-check-circle',
        draft: 'mdi-file-edit-outline',
        review: 'mdi-eye-check',
        approved: 'mdi-check-all',
        archived: 'mdi-archive',
        pending: 'mdi-clock-outline',
        rejected: 'mdi-close-circle'
      }
      return icons[status] || 'mdi-file-document'
    }

    const getStatusText = (status) => {
      const texts = {
        active: 'Active',
        draft: 'Draft',
        review: 'In Review',
        approved: 'Approved',
        archived: 'Archived',
        pending: 'Pending',
        rejected: 'Rejected'
      }
      return texts[status] || status?.charAt(0).toUpperCase() + status?.slice(1) || 'Unknown'
    }

    const formatFileSize = (bytes) => {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    const formatDate = (dateString) => {
      if (!dateString) return 'No date'
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return 'Invalid date'
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }

    const getInitials = (name) => {
      if (!name) return ''
      return name.split(' ').map(n => n[0]).join('').toUpperCase()
    }

    const getFileExtension = (filename) => {
      return filename.split('.').pop().toLowerCase()
    }

    // File handling
    const handleFileSelect = (event) => {
      const files = Array.from(event.target.files)
      selectedFiles.value = [...selectedFiles.value, ...files]
    }

    const handleFileDrop = (event) => {
      event.preventDefault()
      const files = Array.from(event.dataTransfer.files)
      selectedFiles.value = [...selectedFiles.value, ...files]
    }

    const removeFile = (index) => {
      selectedFiles.value.splice(index, 1)
    }

    // Document operations
    const viewDocument = async (document) => {
      try {
        // Load full document details
        const response = await api.get(`/documents/${document.id}`)
        selectedDocument.value = response.data.document
        // Comments are now embedded in the document response
        documentComments.value = response.data.document.comments || []
        // Fallback: if embedded comments is empty but comment_count > 0, fetch separately
        if (documentComments.value.length === 0 && (response.data.document.comment_count || 0) > 0) {
          const commentsResp = await api.get(`/documents/${document.id}/comments`)
          documentComments.value = commentsResp.data.comments || []
        }
        showViewerDialog.value = true

        // Pre-fetch PDF blob with auth so the iframe never needs to make
        // a credentialed request itself (browsers can't set Authorization
        // headers on iframe src).
        if (selectedDocument.value?.file_type === 'pdf') {
          try {
            const blobResp = await api.get(`/documents/${document.id}/content`, { responseType: 'blob' })
            if (previewBlobUrl.value) URL.revokeObjectURL(previewBlobUrl.value)
            previewBlobUrl.value = URL.createObjectURL(blobResp.data)
          } catch (blobErr) {
            console.error('Could not load PDF preview:', blobErr)
            previewBlobUrl.value = null
          }
        } else {
          if (previewBlobUrl.value) URL.revokeObjectURL(previewBlobUrl.value)
          previewBlobUrl.value = null
        }
      } catch (error) {
        console.error('Error loading document details:', error)
        toast.error('Failed to load document details')
      }
    }

    const downloadDocument = async (doc) => {
      try {
        const response = await api.get(`/documents/${doc.id}/download`, {
          responseType: 'blob'
        })
        
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', doc.original_filename || doc.title)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (error) {
        console.error('Error downloading document:', error)
        toast.error('Failed to download document')
      }
    }

    const shareDocument = (document) => {
      selectedDocument.value = document
      shareForm.value = {
        users: [],
        permission: 'view',
        message: ''
      }
      showShareDialog.value = true
    }

    const editDocument = (document) => {
      // Navigate to edit page or open edit dialog
      router.push(`/documents/${document.id}/edit`)
    }

    const deleteDocument = (document) => {
      selectedDocument.value = document
      showDeleteDialog.value = true
    }

    const toggleStar = async (document) => {
      try {
        await api.put(`/documents/${document.id}/star`, {
          starred: !document.is_starred
        })
        document.is_starred = !document.is_starred
        toast.success(document.is_starred ? 'Document starred' : 'Document unstarred')
      } catch (error) {
        console.error('Error toggling star:', error)
        toast.error('Failed to update star status')
      }
    }

    const uploadDocument = async () => {
      if (!uploadFormValid.value || selectedFiles.value.length === 0) return

      uploading.value = true
      try {
        for (const file of selectedFiles.value) {
          const formData = new FormData()
          formData.append('file', file)
          formData.append('title', uploadForm.value.title || file.name)
          formData.append('description', uploadForm.value.description)
          formData.append('category_id', uploadForm.value.category_id)
          formData.append('status', uploadForm.value.status)
          formData.append('access_level', uploadForm.value.access_level)
          if (uploadForm.value.meeting_id) {
            formData.append('meeting_id', uploadForm.value.meeting_id)
          }
          formData.append('tags', JSON.stringify(uploadForm.value.tags))

          const response = await api.post('/documents/', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          })

          documents.value.unshift(response.data.document)
        }

        toast.success(`${selectedFiles.value.length} document(s) uploaded successfully`)
        showUploadDialog.value = false
        resetUploadForm()
      } catch (error) {
        console.error('Error uploading documents:', error)
        toast.error('Failed to upload documents')
      } finally {
        uploading.value = false
      }
    }

    const confirmDelete = async () => {
      deleting.value = true
      try {
        await api.delete(`/documents/${selectedDocument.value.id}`)
        documents.value = documents.value.filter(d => d.id !== selectedDocument.value.id)
        toast.success('Document deleted successfully')
        showDeleteDialog.value = false
      } catch (error) {
        console.error('Error deleting document:', error)
        toast.error('Failed to delete document')
      } finally {
        deleting.value = false
      }
    }

    const resetUploadForm = () => {
      selectedFiles.value = []
      uploadForm.value = {
        title: '',
        description: '',
        category_id: '',
        access_level: 'board_members',
        meeting_id: '',
        tags: []
      }
    }

    const closeViewerDialog = () => {
      showViewerDialog.value = false
      if (previewBlobUrl.value) {
        URL.revokeObjectURL(previewBlobUrl.value)
        previewBlobUrl.value = null
      }
    }

    // Comments
    const loadDocumentComments = async (documentId) => {
      // Comments are now loaded as part of document details in viewDocument()
      // This function is kept for compatibility but no longer makes separate API calls
      try {
        const response = await api.get(`/documents/${documentId}`)
        documentComments.value = response.data.document.comments || []
      } catch (error) {
        console.error('Error loading comments:', error)
        documentComments.value = []
      }
    }

    const addComment = async () => {
      if (!newComment.value.trim()) return

      try {
        const response = await api.post(`/documents/${selectedDocument.value.id}/comments`, {
          content: newComment.value
        })
        
        // Add the new comment to the list
        documentComments.value.push(response.data.comment)
        newComment.value = ''
        toast.success('Comment added successfully')
        
        // Refresh the document to get updated comment count
        const docResponse = await api.get(`/documents/${selectedDocument.value.id}`)
        selectedDocument.value = docResponse.data.document
      } catch (error) {
        console.error('Error adding comment:', error)
        toast.error('Failed to add comment')
      }
    }

    // Category Management Methods
    const addCategory = async () => {
      if (!categoryFormValid.value) {
        console.log('Form is not valid')
        return
      }

      console.log('Adding category:', newCategory.value)
      addingCategory.value = true
      try {
        const response = await api.post('/documents/categories', newCategory.value)
        console.log('Category added successfully:', response.data)
        
        // Add to local data
        categories.value.push(response.data.category)
        
        // Reset form
        newCategory.value = {
          name: '',
          description: '',
          color: '#1565C0'
        }
        
        toast.success('Category added successfully')
      } catch (error) {
        console.error('Error adding category:', error)
        console.error('Response data:', error.response?.data)
        console.error('Response status:', error.response?.status)
        const errorMessage = error.response?.data?.message || 'Failed to add category'
        toast.error(errorMessage)
      } finally {
        addingCategory.value = false
      }
    }

    const editCategory = (category) => {
      editCategoryDialog.value = {
        show: true,
        category: { ...category }
      }
    }

    const updateCategory = async () => {
      if (!editCategoryFormValid.value) return

      updatingCategory.value = true
      try {
        const response = await api.put(`/documents/categories/${editCategoryDialog.value.category.id}`, editCategoryDialog.value.category)
        
        // Update local data
        const index = categories.value.findIndex(c => c.id === editCategoryDialog.value.category.id)
        if (index !== -1) {
          categories.value[index] = response.data.category
        }
        
        editCategoryDialog.value.show = false
        toast.success('Category updated successfully')
      } catch (error) {
        console.error('Error updating category:', error)
        toast.error('Failed to update category')
      } finally {
        updatingCategory.value = false
      }
    }

    const deleteCategory = (category) => {
      deleteCategoryDialog.value = {
        show: true,
        category
      }
    }

    const confirmDeleteCategory = async () => {
      deletingCategory.value = true
      try {
        await api.delete(`/documents/categories/${deleteCategoryDialog.value.category.id}`)
        
        // Remove from local data
        categories.value = categories.value.filter(c => c.id !== deleteCategoryDialog.value.category.id)
        
        deleteCategoryDialog.value.show = false
        toast.success('Category deleted successfully')
      } catch (error) {
        console.error('Error deleting category:', error)
        toast.error('Failed to delete category')
      } finally {
        deletingCategory.value = false
      }
    }

    // Data loading
    const loadDocuments = async () => {
      loading.value = true
      try {
        const response = await api.get('/documents/')
        documents.value = response.data.documents || []
      } catch (error) {
        console.error('Error loading documents:', error)
        toast.error('Failed to load documents')
      } finally {
        loading.value = false
      }
    }

    const loadCategories = async () => {
      try {
        const response = await api.get('/documents/categories')
        categories.value = response.data.categories || []
      } catch (error) {
        console.error('Error loading categories:', error)
        categories.value = []
      }
    }

    const loadMeetings = async () => {
      try {
        const response = await api.get('/meetings/')
        meetings.value = response.data.meetings || []
      } catch (error) {
        console.error('Error loading meetings:', error)
      }
    }

    const loadBoardMembers = async () => {
      try {
        const response = await api.get('/members/')
        boardMembers.value = response.data.members || []
      } catch (error) {
        console.error('Error loading board members:', error)
      }
    }

    // Lifecycle
    onMounted(() => {
      loadDocuments()
      loadCategories()
      loadMeetings()
      loadBoardMembers()
    })

    return {
      // State
      loading,
      documents,
      categories,
      meetings,
      boardMembers,
      viewMode,
      searchQuery,
      categoryFilter,
      typeFilter,
      statusFilter,
      quickFilter,
      isAdmin,

      // Dialog states
      showUploadDialog,
      showViewerDialog,
      showShareDialog,
      showDeleteDialog,
      showCategoriesDialog,
      selectedDocument,
      uploading,
      deleting,

      // Category Management
      categoryFormValid,
      editCategoryFormValid,
      addingCategory,
      updatingCategory,
      deletingCategory,
      newCategory,
      editCategoryDialog,
      deleteCategoryDialog,
      categoryNameRules,

      // Upload form
      uploadFormValid,
      selectedFiles,
      uploadForm,

      // Comments
      documentComments,
      newComment,

      // Share form
      shareForm,

      // Reference data
      documentTypes,
      statusOptions,
      accessLevels,
      sharePermissions,
      listHeaders,

      // Validation rules
      titleRules,
      categoryRules,
      accessRules,

      // Computed
      filteredDocuments,

      // Methods
      clearFilters,
      getDocumentIcon,
      getDocumentColor,
      getStatusColor,
      getStatusIcon,
      getStatusText,
      formatFileSize,
      formatDate,
      getInitials,
      getFileExtension,
      handleFileSelect,
      handleFileDrop,
      removeFile,
      viewDocument,
      downloadDocument,
      shareDocument,
      editDocument,
      deleteDocument,
      toggleStar,
      uploadDocument,
      confirmDelete,
      resetUploadForm,
      previewBlobUrl,
      closeViewerDialog,
      loadDocumentComments,
      addComment,
      loadDocuments,

      // Category Management Methods
      addCategory,
      editCategory,
      updateCategory,
      deleteCategory,
      confirmDeleteCategory
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/variables.scss";

.documents-page {
  .text-medium-emphasis {
    opacity: 0.7;
  }

  .text-primary {
    color: rgb(var(--v-theme-primary)) !important;
    
    &:hover {
      text-decoration: underline !important;
    }
  }

  // Document Grid Styles
  .document-card {
    height: 100%;
    transition: transform 0.2s, box-shadow 0.2s;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
    }

    .document-preview {
      position: relative;
      height: 120px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, rgba(var(--v-theme-primary), 0.1), rgba(var(--v-theme-secondary), 0.1));

      .document-overlay {
        position: absolute;
        top: 8px;
        right: 8px;
        opacity: 0;
        transition: opacity 0.2s;
      }

      &:hover .document-overlay {
        opacity: 1;
      }
    }

    .document-title {
      font-weight: 600;
      font-size: 0.875rem;
      line-height: 1.2;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      -webkit-box-orient: vertical;
    }

    .document-meta {
      margin-top: 4px;
    }
  }

  // Upload Dialog Styles
  .upload-area {
    border: 2px dashed rgba(var(--v-theme-primary), 0.3);
    border-radius: 8px;
    padding: 32px;
    text-align: center;
    transition: border-color 0.2s, background-color 0.2s;

    &:hover {
      border-color: rgba(var(--v-theme-primary), 0.5);
      background-color: rgba(var(--v-theme-primary), 0.02);
    }

    .upload-placeholder {
      cursor: pointer;
    }

    .selected-files {
      .file-item {
        display: flex;
        align-items: center;
        padding: 12px;
        border: 1px solid rgba(0, 0, 0, 0.12);
        border-radius: 8px;
        margin-bottom: 8px;

        .file-info {
          flex: 1;
          margin-left: 12px;
          text-align: left;

          .file-name {
            font-weight: 500;
            font-size: 0.875rem;
          }

          .file-size {
            color: rgba(0, 0, 0, 0.6);
          }
        }
      }
    }
  }

  // Document Viewer Styles
  .document-viewer {
    .preview-placeholder {
      height: 400px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background-color: rgba(0, 0, 0, 0.02);
    }
  }

  // Comments Styles
  .comment-item {
    padding: 12px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 8px;
    background-color: rgba(0, 0, 0, 0.02);
  }

  // List View Cursor
  .cursor-pointer {
    cursor: pointer;
  }

  // Category Management Styles
  .category-item {
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    margin-bottom: 8px;
    
    &:hover {
      background-color: rgba(var(--v-theme-primary), 0.04);
    }
  }

  .category-color {
    width: 24px;
    height: 24px;
    border-radius: 4px;
    margin-right: 8px;
    border: 1px solid rgba(0, 0, 0, 0.12);
  }
}
</style>